/**
 * AN INVOICE, AS THE SCREENS NAME IT, AND WHAT THERE IS TO PAY ON IT FROM HERE, in one place, so
 * Payments and an invoice's own page say the same of the same invoice. Both read ops's fields and
 * nothing of their own: what there is to pay is ops's flag and ops's balance (CLAUDE.md binding
 * rule 1).
 *
 * WHICH HOME, provisional P45: an invoice is named under its heading by its clean's home, as a
 * visit is (`homeNameOf`, P40), only when she may see several homes; and by the number ops gives it
 * at its first send (its section 11.2, `invoiceNumbered`); and two on Payments still named alike,
 * the same day at the same home or at none named with the same number or none, each say their first
 * line as ops wrote it, where those differ (`tellApart`).
 */
import type { HomeName, Invoice, InvoicePay } from '../api.ts';
import {
  dollars,
  dueOnDay,
  invoiceOf,
  partPaid,
  PAYING,
  sentByZelleOn,
  visitOf,
  wasDueOn,
} from '../words.ts';
import { dateInWords, dayInWords, daysSince } from './dates.ts';
import { homeNameOf } from './visits.ts';

/**
 * The day an invoice is known by: its clean's day, or the day it was issued when ops could not read
 * the visit's. Null when it has neither as a real day.
 */
export function daysOf(
  invoice: Invoice,
): { readonly visit: string } | { readonly issued: string } | null {
  const visit = invoice.visitDate === undefined ? null : dayInWords(invoice.visitDate);
  if (visit !== null) return { visit };
  const issued = invoice.issuedOn === undefined ? null : dayInWords(invoice.issuedOn);
  return issued === null ? null : { issued };
}

/**
 * What an invoice is called: ARTBOARD "Visit of Tuesday 13 October", or NEW "Invoice of Wednesday
 * 14 October" when ops could not read the visit's day. Null with neither.
 */
export function invoiceHeading(invoice: Invoice): string | null {
  const days = daysOf(invoice);
  if (days === null) return null;
  return 'visit' in days ? visitOf(days.visit) : invoiceOf(days.issued);
}

/** The first line's words a card draws: its first line with a description, as ops wrote it. */
const firstLineOf = (invoice: Invoice): string | undefined =>
  invoice.lines.find((line) => line.description !== undefined)?.description;

/**
 * WHAT TELLS AN INVOICE FROM ANOTHER OF THE SAME NAME, on Payments, by its id: for each invoice of a
 * list known by the same heading, the same home's name and the same number as another
 * (`invoiceHeading`, `homeNameOf`, and the number ops gives it at its first send, its section
 * 11.2, which the card and the row draw), the words of its first line (`firstLineOf`), wherever
 * those of the alike ones are not all the same; nothing for any other invoice, and nothing where
 * they are, since then they tell nothing apart. So in practice two sent before numbering began,
 * which carry none. Only ops's own words: its id is not a screen's, and home makes up no number.
 * Never the amount, which is the largest figure on each card and on each paid row already, so two
 * that differ in it read apart without it.
 */
export function tellApart(
  invoices: readonly Invoice[],
  homes: readonly HomeName[],
): ReadonlyMap<string, string> {
  const byName = new Map<string, Invoice[]>();
  for (const invoice of invoices) {
    const name = JSON.stringify([
      invoiceHeading(invoice),
      homeNameOf(invoice, homes) ?? null,
      invoice.number ?? null,
    ]);
    const alike = byName.get(name);
    if (alike) alike.push(invoice);
    else byName.set(name, [invoice]);
  }
  const apart = new Map<string, string>();
  for (const alike of byName.values()) {
    if (alike.length < 2 || new Set(alike.map(firstLineOf)).size < 2) continue;
    for (const invoice of alike) {
      const line = firstLineOf(invoice);
      if (line !== undefined) apart.set(invoice.id, line);
    }
  }
  return apart;
}

/** Ops's states in which an invoice is owed and not yet overdue, which say the day it is due. */
const DUE: ReadonlySet<string> = new Set(['sent', 'part_paid']);

/**
 * Part paid, with what is still to pay of its total, or null for any other state or no balance: on
 * its card and its own page under the amount, and on its row under On its way (round six, slice S1).
 */
export function partPaidOf({ state, balanceCents, totalCents }: Invoice): string | null {
  if (state !== 'part_paid' || balanceCents === undefined) return null;
  return partPaid(
    dollars(balanceCents),
    totalCents === undefined ? undefined : dollars(totalCents),
  );
}

/**
 * The day an invoice owed is due, as ops gives it: "Due on Friday 23 October." while sent or part
 * paid, and "This was due on 21 October." once overdue. Null for any other state, or a day that is
 * not a real one.
 */
function dueDayOf({ state, dueOn }: Invoice): string | null {
  if (state === undefined || dueOn === undefined) return null;
  if (DUE.has(state)) {
    const day = dayInWords(dueOn);
    return day === null ? null : dueOnDay(day);
  }
  if (state === 'overdue') {
    const day = dateInWords(dueOn);
    return day === null ? null : wasDueOn(day);
  }
  return null;
}

/**
 * What ops's state says of an invoice still owed, in words, the same on its card on Payments and on
 * its own page, under the amount: sent, the day it is due; part paid, what is still to pay of its
 * total, then the day it is due; overdue, the day it was due. Null for any other state, and each
 * part is left out without a balance or a real day.
 *
 * `onItsWay`: what says it is on its way (`onItsWayLine`), said in place of the day it is due,
 * whatever the state, until ops's push says how it stands. The amount above it is still ops's
 * balance, and nothing here reads paid (CLAUDE.md binding rule 1).
 */
export function owedStateOf(invoice: Invoice, onItsWay: string | null = null): string | null {
  const said = [partPaidOf(invoice), onItsWay ?? dueDayOf(invoice)].filter(
    (part): part is string => part !== null,
  );
  return said.length === 0 ? null : said.join(' ');
}

/**
 * WHAT SAYS AN INVOICE OWED IS ON ITS WAY, in place of the day it is due, or null where it is not:
 *
 * - somebody on the household reported sending it by Zelle, and the Worker says her report holds it
 *   on its way (`zelleReport`, ops's section 11.7, round five's slice B2, P53), which it says only
 *   of an invoice still owed, where she pays it herself or pays it by Zelle in place of her saved
 *   method; said with the day she picked, and, for another client's report, without saying who
 *   (`sentByZelleOn`); a day that is not a real one is left out;
 * - else she came back from paying it on Stripe's page, in this tab or another of her browser's,
 *   as the place draws it (`browserHeld`, `lib/paying.ts`, P35), while there is something to pay on
 *   it from here (`payableHere`).
 *
 * Nothing here reads paid (CLAUDE.md binding rule 1).
 */
export function onItsWayLine(invoice: Invoice, browserHeld: boolean): string | null {
  const report = invoice.zelleReport;
  if (report?.state === 'on_its_way') {
    const day = report.sentOn === undefined ? null : dayInWords(report.sentOn);
    return sentByZelleOn(day, report.own);
  }
  return browserHeld && payableHere(invoice) !== null ? PAYING.onItsWay : null;
}

/** The most days the step "I sent it by Zelle" opens offers to pick from: the last seven. */
export const ZELLE_DAYS_OFFERED = 7;

/**
 * THE DAYS SHE MAY SAY SHE SENT ITS ZELLE ON (ops's section 11.7): not after today in Florida,
 * `today`, the place's own day, and not before the day of the clean the invoice bills, its
 * `visitDate`, or the day it was issued where ops gives no clean, since a Zelle sent on the evening
 * of the clean is a real one and the clean's day is the earliest day money for it could be sent. At
 * most the last seven such days, newest first, so today comes first and is the one chosen; with
 * neither day from ops, the last seven. None for a clean still to come, or a `today` that is not a
 * real day. The request door holds the same bounds (`api/src/seam/requests.ts`, `reportRefusal`).
 */
export function zelleSentDays(
  invoice: Pick<Invoice, 'visitDate' | 'issuedOn'>,
  today: string,
): string[] {
  if (dayInWords(today) === null) return [];
  const earliest = invoice.visitDate ?? invoice.issuedOn;
  const since = earliest === undefined ? null : daysSince(earliest, today);
  if (earliest !== undefined && (since === null || since < 0)) return [];
  const offered = Math.min(ZELLE_DAYS_OFFERED, since === null ? ZELLE_DAYS_OFFERED : since + 1);
  const [year, month, date] = today.split('-').map(Number) as [number, number, number];
  return Array.from({ length: offered }, (_, back) =>
    new Date(Date.UTC(year, month - 1, date - back)).toISOString().slice(0, 10),
  );
}

/**
 * What there is to pay on the invoice from here now, in cents, or null for nothing: only where ops
 * says it is payable from home, no bank payment on it is clearing, no saved method pays it, ops
 * gives a balance above nothing, and ops has not said since its push that it owes nothing
 * (`owesNothingSaid`).
 */
export function payableHere(invoice: Invoice): number | null {
  const { pay, autopay, balanceCents } = invoice;
  if (!pay.payable || pay.clearing || autopay !== undefined || owesNothingSaid(invoice)) {
    return null;
  }
  return balanceCents !== undefined && balanceCents > 0 ? balanceCents : null;
}

/**
 * Whether ops refused her taking back her Zelle report saying the invoice owes nothing now
 * (`400 invoice_not_open`, ops's section 11.7, "Taking it back", P53), which the Worker carries only
 * while its push has not caught up with the refusal: nothing is offered to pay on it, Payments'
 * card leads to its page without Pay, and its page says the refusal's words in place of every way
 * to pay, until the push says how it stands.
 */
export const owesNothingSaid = (invoice: Pick<Invoice, 'zelleReport'>): boolean =>
  invoice.zelleReport?.state === 'taken_back' && invoice.zelleReport.code === 'invoice_not_open';

/**
 * Whether ops offers a way to pay the invoice from home: a Zelle tag, or the bank and card screens.
 * With neither, its own page gives only the office's number (`screens/Invoice.tsx`, `WaysToPay`),
 * so its card on Payments offers no Pay.
 */
export const wayFromHome = (pay: InvoicePay): boolean => pay.online || pay.zelle !== undefined;
